// Generated from scripts/fly-to-cart.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { yieldToMainThread } from '@theme/utilities';
/**
 * The cart item of the bottom navigation. It counts only while its bar reports itself with
 * `data-edge-visible`; whether the bar is on screen right now is read from geometry in `isOnScreen`.
 */
const BAR_DESTINATION = '[data-edge-surface="bottom-nav"][data-edge-visible="true"] [data-cart-target]';
/** The cart icon of the header's cart block. */
const HEADER_DESTINATION = '.header-actions__cart-icon';
/**
 * Whether the shopper can see the element: it has a box (not `display: none`), it is not hidden by
 * `visibility` (a bar that yields to another bar or hides on scroll), and the box lies in the viewport (a bar
 * that slid off the edge keeps its layout box).
 */
function isOnScreen(element) {
    const box = element.getBoundingClientRect();
    if (box.width === 0 || box.height === 0)
        return false;
    if (getComputedStyle(element).visibility !== 'visible')
        return false;
    return box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth;
}
/**
 * Where an add-to-cart motion should land: the bottom navigation's cart item while that bar is on screen,
 * otherwise the header's cart icon. Null when neither exists.
 */
function resolveDestination() {
    for (const bar of document.querySelectorAll(BAR_DESTINATION)) {
        if (isOnScreen(bar))
            return bar;
    }
    return document.querySelector(HEADER_DESTINATION);
}
/**
 * Animates a product image from `source` to the cart when something is added.
 *
 * The caller creates the element, sets `source` (and `useSourceSize` when the image keeps the source's
 * size), classes the motion, and appends it to the body. `destination` is optional: without it the element
 * resolves the visible cart target when it connects, so the bottom navigation's cart item and the header
 * icon are chosen in one place. It removes itself when the animation ends, and at once when it has no
 * source or no destination.
 *
 * The properties are declared without an initializer so a value assigned before the element upgrades
 * survives the upgrade.
 */
class FlyToCart extends Component {
    connectedCallback() {
        super.connectedCallback();
        const { source } = this;
        const destination = this.destination ?? resolveDestination();
        if (!source || !destination) {
            this.remove();
            return;
        }
        this.destination = destination;
        const observer = new IntersectionObserver((entries) => {
            observer.disconnect();
            let sourceRect = null;
            let destinationRect = null;
            for (const entry of entries) {
                if (entry.target === source)
                    sourceRect = entry.boundingClientRect;
                else if (entry.target === destination)
                    destinationRect = entry.boundingClientRect;
            }
            if (sourceRect && destinationRect)
                void this.#animate(sourceRect, destinationRect);
            else
                this.remove();
        });
        observer.observe(source);
        observer.observe(destination);
    }
    /** Moves the element along its bezier curve from the center of the source to the center of the destination. */
    #animate = async (sourceRect, destinationRect) => {
        const startX = sourceRect.left + sourceRect.width / 2;
        const startY = sourceRect.top + sourceRect.height / 2;
        const endX = destinationRect.left + destinationRect.width / 2;
        const endY = destinationRect.top + destinationRect.height / 2;
        if (this.useSourceSize) {
            this.style.setProperty('--width', `${sourceRect.width}px`);
            this.style.setProperty('--height', `${sourceRect.height}px`);
        }
        this.style.setProperty('--start-x', `${startX}px`);
        this.style.setProperty('--start-y', `${startY}px`);
        this.style.setProperty('--travel-x', `${endX - startX}px`);
        this.style.setProperty('--travel-y', `${endY - startY}px`);
        await yieldToMainThread();
        await Promise.allSettled(this.getAnimations().map((animation) => animation.finished));
        this.remove();
    };
}
if (!customElements.get('fly-to-cart')) {
    customElements.define('fly-to-cart', FlyToCart);
}
